"use client"

import { useRef } from "react"
import { Heading, Text } from "@chakra-ui/react"
import { Box } from "@chakra-ui/react/box"
import { Flex } from "@chakra-ui/react/flex"
import { Link } from "@chakra-ui/react/link"
import { HStack } from "@chakra-ui/react/stack"
import { VStack } from "@chakra-ui/react/stack"
import { type HTMLReactParserOptions } from "html-react-parser"
import { useInView } from "framer-motion"

import BlurReveal from "@/components/Animations/BlurReveal"
import BlurText from "@/components/Animations/BlurText"
import ErrorMessage from "@/components/ErrorMessage"
import LogoLoop, { type LogoItem } from "@/components/LogoLoop/LogoLoop"
import { Company } from "@/types/database"
import {
  getSafeHtmlParserOptions,
  isParserElementNode,
} from "@/utils/getSafeHtmlParserOptions"

interface CompanyMarqueeItemProps {
  company: Company
  parserOptions: HTMLReactParserOptions
}

const CompanyMarqueeItem = ({ company }: CompanyMarqueeItemProps) => {
  return (
    <Flex
      alignItems="center"
      className="marquee_company_item_wrapper"
      justifyContent="center"
    >
      <Link
        _focusVisible={{
          outline: "1px solid {colors.primary.400}",
          outlineOffset: "8px",
        }}
        _hover={{
          backgroundSize: "100% 1px",
          color: "fg",
          textDecoration: "none",
        }}
        aria-label={company.name}
        backgroundImage="linear-gradient(currentColor, currentColor)"
        backgroundPosition="0 100%"
        backgroundRepeat="no-repeat"
        backgroundSize={"0% 1px"}
        className="marquee_company_item"
        color={"fg.muted"}
        href={company.url}
        overflow="visible"
        py={"2px"}
        rel="noopener noreferrer"
        target="_blank"
        title={company.name}
        transition="color 180ms ease, background-size 300ms cubic-bezier(.2,.7,.2,1)"
      >
        <VStack
          className="logo_container"
          justifyContent={"center"}
          position="relative"
          zIndex="1"
        >
          <Text
            as="label"
            color="currentColor"
            cursor="pointer"
            fontFamily={"mono"}
            fontSize={"xs"}
            textAlign={"center"}
          >
            {company.name}
          </Text>
        </VStack>
      </Link>
    </Flex>
  )
}

const CompaniesMarquee2: React.FC<{ data: Company[]; label: string }> = ({
  data,
  label,
}) => {
  const sectionRef = useRef<HTMLDivElement>(null)
  const isInView = useInView(sectionRef, { once: true, margin: "-40px" })

  const hasValidCompanies = data && Array.isArray(data) && data.length > 0
  const parserOptions: HTMLReactParserOptions = getSafeHtmlParserOptions(
    (domNode) => {
      if (isParserElementNode(domNode) && domNode.attribs?.class == "width") {
        return ""
      }
      if (isParserElementNode(domNode) && domNode.attribs?.class == "height") {
        return ""
      }
    }
  )

  const logos: LogoItem[] = hasValidCompanies
    ? data.map((company) => ({
        href: company.url,
        node: (
          <>
            <CompanyMarqueeItem
              company={company}
              parserOptions={parserOptions}
            />{" "}
          </>
        ),
        title: company.name,
      }))
    : []

  return (
    <Box
      alignItems="stretch"
      as="section"
      borderBottom={"1px solid {colors.border}"}
      flexWrap="nowrap"
      justifyContent="flex-start"
      position="relative"
      ref={sectionRef}
      width={"full"}
      zIndex={1}
    >
      {hasValidCompanies ? (
        <HStack
          borderRight={"1px solid {colors.border}"}
          gap={0}
          maxWidth="1440px"
          mx="auto"
          position="relative"
          width="100%"
          zIndex={1}
        >
          {/* Label — word-by-word blur reveal */}
          <HStack
            alignItems="center"
            borderLeft={"1px solid {colors.border}"}
            borderRight={"1px solid {colors.border}"}
            className="preface"
            flexShrink={0}
            height="100%"
            justifyContent={"center"}
            position="relative"
            px={4}
            py={4}
          >
            <Heading
              _empty={{ display: "none" }}
              as="p"
              className="preface-text"
              color={"fg.muted"}
              display="inline"
              fontFamily={"code"}
              fontSize={{ base: "sm", md: "sm" }}
              fontWeight="500"
              letterSpacing="0"
              lineHeight="1.25"
            >
              <BlurText animateWhen={isInView} stagger={0.1} text={label} />
            </Heading>
          </HStack>

          {/* Marquee — block blur reveal, starts after label */}
          <BlurReveal
            animateWhen={isInView}
            blur={8}
            delay={0.35}
            duration={0.7}
            style={{ flex: 1, minWidth: 0, overflow: "hidden" }}
          >
            <LogoLoop
              ariaLabel="Companies I have worked with"
              direction="left"
              gap={64}
              hoverSpeed={0}
              logoHeight={104}
              logos={logos}
              renderItem={(item) => {
                if ("node" in item) return item.node
                return null
              }}
              scaleOnHover
              speed={32}
            />
          </BlurReveal>
        </HStack>
      ) : (
        <VStack
          borderColor="neutral.200"
          mt={0}
          position="relative"
          rounded={"2xl"}
        >
          <ErrorMessage
            message="Please try to refresh the page."
            status="error"
            title="Unable to locate the companies logos!"
          />
        </VStack>
      )}
    </Box>
  )
}

export default CompaniesMarquee2
